(() => {
  'use strict';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const variantId = value => String(value?.id ?? value ?? '').split('/').pop();
  const quantity = value => Math.max(0, Math.min(99, Math.floor(Number(value) || 0)));
  const config = () => window.RI_SHOPIFY || {};
  const route = path => (window.Shopify?.routes?.root || config().root || '/').replace(/\/?$/, '/') + path;
  const catalog = new Map();
  let cart = null;
  let drawer;
  let pending = 0;
  let queue = Promise.resolve();

  window.RIGifts = { forVariant: () => [], sync: () => Promise.resolve() };

  function money(cents) {
    const currency = cart?.currency || config().currency || window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  }

  function readCatalog() {
    const main = config().main || [];
    document.querySelectorAll('#opts input[data-id]').forEach(input => {
      const matched = main.find(variant => variantId(variant.id) === variantId(input.dataset.id));
      if (!matched) return;
      catalog.set(variantId(matched.id), { ...matched, tier: Number(input.dataset.tier), image: matched.image || input.closest('.opt')?.querySelector('img')?.getAttribute('src') || '' });
    });
  }

  async function request(path, data) {
    const response = await fetch(route(path), {
      method: data ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(data ? { 'Content-Type': 'application/json' } : {}) },
      ...(data ? { body: JSON.stringify(data) } : {})
    });
    let result;
    try { result = await response.json(); } catch (_) { throw new Error('The store could not load your cart. Please try again.'); }
    if (!response.ok || result.status >= 400) {
      const message = result.description || result.message || 'The store could not update your cart. Please try again.';
      throw new Error(typeof message === 'string' ? message : 'The store could not update your cart. Please try again.');
    }
    return result;
  }

  function applyCart(next) {
    if (!Array.isArray(next?.items)) throw new Error('The store returned an incomplete cart. Please try again.');
    cart = next;
    drawer?.render();
    document.dispatchEvent(new CustomEvent('ri:cart-change', { detail: next }));
    return next;
  }

  function refresh() { return request('cart.js').then(applyCart); }

  function enqueue(operation, showError = true) {
    pending += 1;
    drawer?.setPending();
    const task = queue.then(operation);
    queue = task.catch(() => {}).finally(() => {
      pending -= 1;
      drawer?.setPending();
    });
    return task.catch(error => {
      if (showError) drawer?.showError(error.message);
      throw error;
    });
  }

  function knownVariant(id) {
    return (config().main || []).some(item => variantId(item.id) === id) || Object.values(config().accessories || {}).some(item => variantId(item) === id);
  }

  function add(id, count = 1, trigger) {
    const actualId = variantId(id);
    return enqueue(async () => {
      drawer?.clearError();
      if (!/^\d+$/.test(actualId) || !knownVariant(actualId)) throw new Error('This product is not available to purchase yet.');
      if (!quantity(count)) throw new Error('Choose a quantity of at least one.');
      await request('cart/add.js', { items: [{ id: actualId, quantity: quantity(count) }] });
      try { await refresh(); }
      catch (_) { throw new Error('Your item was added, but the cart could not refresh. Open your cart before adding it again.'); }
      drawer?.open(trigger, false);
      return cart;
    }).catch(error => { drawer?.open(trigger, false); throw error; });
  }

  function includedMarkup(item) {
    const product = catalog.get(variantId(item.variant_id));
    if (!product || product.tier < 1) return '';
    let html = '<div class="recreation-cart-included"><span class="recreation-cart-warranty">✓ 2-year warranty included</span>';
    if (product.tier === 2) {
      document.querySelectorAll('#gifts .g[data-min="2"]').forEach(gift => {
        const name = gift.querySelector('.gt b')?.textContent || 'Bundle inclusion';
        const image = gift.querySelector('img')?.getAttribute('src') || '';
        html += '<div class="recreation-cart-gift">' + (image ? '<img src="' + escape(image) + '" width="32" height="32" alt="">' : '') + '<span>' + escape(name) + (item.quantity > 1 ? ' × ' + item.quantity : '') + '</span><b class="cartitem-free-product-label">Included</b></div>';
      });
    }
    return html + '</div>';
  }

  class ShopifyCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.recreationReady) return;
      this.dataset.recreationReady = 'true';
      this.classList.add('recreation-cart');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.dialog = this.querySelector('.drawer__inner');
      this.empty = this.querySelector('.drawer__inner-empty');
      this.rows = this.querySelector('#CartDrawer-CartItems');
      if (!this.dialog || !this.rows) return;
      this.dialog.setAttribute('tabindex', '-1');
      this.dialog.setAttribute('role', 'dialog');
      this.dialog.setAttribute('aria-modal', 'true');
      this.dialog.setAttribute('aria-label', 'Your cart');
      this.header = document.createElement('div');
      this.header.className = 'drawer__header recreation-cart-header';
      this.header.innerHTML = '<h2 class="drawer__heading">Your cart <span class="recreation-cart-header-count"></span></h2><button type="button" class="drawer__close" aria-label="Close cart"><span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path d="m1 1 16 15M17 1 1 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span></button>';
      this.dialog.insertBefore(this.header, this.dialog.firstChild);
      this.note = document.createElement('p');
      this.note.className = 'ri-cart-status';
      this.note.setAttribute('role', 'alert');
      this.note.hidden = true;
      this.header.after(this.note);
      const form = this.querySelector('#CartDrawer-Form');
      if (form) { form.action = route('cart'); form.method = 'post'; }
      const checkout = this.querySelector('#CartDrawer-Checkout');
      if (checkout) { checkout.type = 'submit'; checkout.name = 'checkout'; checkout.setAttribute('form', 'CartDrawer-Form'); }
      this.live = document.createElement('p');
      this.live.className = 'visually-hidden';
      this.live.setAttribute('aria-live', 'polite');
      this.dialog.append(this.live);
      this.addEventListener('click', event => this.handleClick(event));
      this.addEventListener('keydown', event => this.handleKeydown(event));
      form?.addEventListener('submit', event => { if (pending || !cart?.item_count) event.preventDefault(); });
    }

    showError(message) {
      this.note.hidden = false;
      this.note.textContent = message;
    }

    clearError() { this.note.hidden = true; this.note.textContent = ''; }

    setPending() {
      this.setAttribute('aria-busy', pending ? 'true' : 'false');
      this.querySelectorAll('[data-action]').forEach(button => { button.disabled = !!pending; });
      this.querySelectorAll('[data-action="increase"]').forEach(button => {
        const item = cart?.items.find(entry => entry.key === button.closest('[data-cart-key]')?.dataset.cartKey);
        button.disabled = !!pending || item?.quantity >= 99;
      });
      const checkout = this.querySelector('#CartDrawer-Checkout');
      if (checkout) checkout.disabled = !!pending || !cart?.item_count;
      document.querySelectorAll('#atc, #sbtn, .pairs .pc button').forEach(button => {
        button.disabled = !!pending;
        button.setAttribute('aria-busy', pending ? 'true' : 'false');
      });
    }

    render() {
      if (!this.rows || !cart) return;
      const empty = !cart.item_count;
      this.classList.toggle('is-empty', empty);
      this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
      if (this.empty) this.empty.hidden = !empty;
      this.header.hidden = empty;
      this.header.querySelector('.recreation-cart-header-count').textContent = '(' + cart.item_count + ')';
      this.rows.innerHTML = '<div class="cart-items recreation-cart-items">' + cart.items.map(item => {
        const title = escape(item.product_title || item.title);
        const image = item.image || item.featured_image?.url || catalog.get(variantId(item.variant_id))?.image || '';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        const current = item.final_line_price ?? item.line_price;
        const original = item.original_line_price ?? item.line_price;
        return '<article class="cart-item recreation-cart-item" data-cart-key="' + escape(item.key) + '"><div class="cart-item__media">' + (image ? '<img class="cart-item__image" src="' + escape(image) + '" alt="' + title + '" width="84" height="84">' : '') + '</div><div class="cart-item__details"><div class="recreation-cart-item-heading"><h3 class="cart-item__name">' + title + '</h3><button type="button" class="cart-remove-button recreation-cart-remove" data-action="remove" aria-label="Remove ' + title + '"><svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3.5 5.5h13m-11.5 0 .8 11h8.4l.8-11M7.5 5.5v-3h5v3M8 8.5v5.5m4-5.5v5.5"/></svg></button></div>' + (variant ? '<p class="product-option recreation-cart-variant">' + escape(variant) + '</p>' : '') + '<div class="gb-price-drawer recreation-cart-price">' + (original > current ? '<s>' + escape(money(original)) + '</s>' : '') + '<strong class="cart-item__final-price">' + escape(money(current)) + '</strong></div><div class="cart-item__quantity recreation-cart-quantity"><button type="button" data-action="decrease" aria-label="Decrease quantity of ' + title + '">−</button><span aria-label="Quantity">' + Number(item.quantity) + '</span><button type="button" data-action="increase" aria-label="Increase quantity of ' + title + '">+</button></div>' + includedMarkup(item) + '</div></article>';
      }).join('') + '</div>';
      this.querySelectorAll('.totals__total-value').forEach(el => { el.textContent = money(cart.total_price); });
      const total = this.querySelector('.gb-totals-total-value');
      if (total) total.textContent = cart.total_price;
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.setAttribute('aria-label', 'Cart, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
        let badge = icon.querySelector('.cart-count-bubble');
        if (!empty && !badge) { badge = document.createElement('div'); badge.className = 'cart-count-bubble'; icon.append(badge); }
        if (badge) { badge.hidden = empty; badge.innerHTML = '<span aria-hidden="true">' + Number(cart.item_count) + '</span>'; }
      }
      this.live.textContent = cart.item_count + ' items in your cart. Total ' + money(cart.total_price) + '.';
      this.setPending();
    }

    open(trigger, shouldRefresh = true) {
      if (!this.classList.contains('active')) {
        this.returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.inert = false;
        this.setAttribute('aria-hidden', 'false');
        this.classList.add('active', 'animate');
        this.background = [];
        for (const node of document.body.children) {
          if (node instanceof HTMLElement && !node.contains(this) && node !== this && !['SCRIPT', 'STYLE', 'LINK'].includes(node.tagName)) {
            this.background.push([node, node.inert]);
            node.inert = true;
          }
        }
        requestAnimationFrame(() => this.dialog.focus({ preventScroll: true }));
      }
      if (shouldRefresh) enqueue(async () => { this.clearError(); return refresh(); }).catch(() => {});
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active');
      document.body.style.overflow = this.previousOverflow || '';
      for (const [node, inert] of this.background || []) node.inert = inert;
      this.background = [];
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }

    handleClick(event) {
      if (event.target.closest('.drawer__close, #CartDrawer-Overlay')) { event.preventDefault(); this.close(); return; }
      if (event.target.closest('.cart-drawer__empty-content a[href*="/products/"]')) { event.preventDefault(); this.close(); document.getElementById('buy')?.scrollIntoView({ behavior: 'smooth' }); return; }
      const button = event.target.closest('button[data-action]');
      if (!button || pending) return;
      const key = button.closest('[data-cart-key]')?.dataset.cartKey;
      const action = button.dataset.action;
      const item = cart?.items.find(entry => entry.key === key);
      if (!item || !['remove', 'decrease', 'increase'].includes(action)) return;
      event.preventDefault();
      enqueue(async () => {
        this.clearError();
        const count = action === 'remove' ? 0 : item.quantity + (action === 'increase' ? 1 : -1);
        applyCart(await request('cart/change.js', { id: key, quantity: quantity(count) }));
        const replacement = Array.from(this.querySelectorAll('[data-cart-key]')).find(entry => entry.dataset.cartKey === key)?.querySelector('[data-action="' + action + '"]');
        requestAnimationFrame(() => (replacement || this.querySelector(this.classList.contains('is-empty') ? '.cart-drawer__empty-content .drawer__close' : '.recreation-cart-header .drawer__close'))?.focus());
      }).catch(() => {});
    }

    handleKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.querySelectorAll('button, a[href], input, [tabindex="0"]')).filter(el => !el.disabled && !el.closest('[hidden]') && el.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (!first) { event.preventDefault(); this.dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  function init() {
    readCatalog();
    if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', ShopifyCartDrawer);
    drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.setPending !== 'function') return;
    document.addEventListener('submit', event => {
      if (event.target !== document.getElementById('ri-product-form')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pending) return;
      const selected = document.querySelector('#opts input:checked');
      const count = quantity(document.getElementById('qv')?.textContent || document.getElementById('ri-qty')?.value || 1);
      add(selected?.dataset.id, count, document.getElementById('atc')).catch(() => {});
    }, true);
    document.addEventListener('click', event => {
      const button = event.target.closest('.pairs .pc button, [data-add]');
      if (button && !drawer.contains(button)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (pending) return;
        const sourceId = button.dataset.sourceVariantId || button.dataset.add || button.dataset.variantId || '';
        const mapped = config().accessories?.[sourceId];
        add(mapped, 1, button).catch(() => {});
        return;
      }
      const anchor = event.target.closest('a[href]');
      if (!anchor) return;
      const url = new URL(anchor.href, location.href);
      if (/\/cart\/?$/.test(url.pathname) && (url.origin === location.origin || /^(www\.)?rosyiron\.com$/.test(url.hostname))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        drawer.open(anchor);
      }
    }, true);
    window.addEventListener('pageshow', event => { if (event.persisted) enqueue(refresh, false).catch(() => {}); });
    window.RIShopifyCart = { add, refresh: () => enqueue(refresh), snapshot: () => cart, open: () => drawer.open(), close: () => drawer.close() };
    enqueue(refresh, false).catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
